import { useEffect, useState } from "react";
import styled from "styled-components";
import theme from "../../styles/theme";
import SelectBox from "../../components/SelectBox";
import { Checkbox, FormControlLabel, Radio, RadioGroup, Button, Icon } from "@mui/material";
import axios from "axios";
import { components } from "react-select";
import { Icon as MuiIcon } from "@mui/material";

const Container = styled.div`
  margin: 20px 40px;
`;

const OptionCard = styled.div`
  background: ${theme.color.blue50};
  border: 1px solid ${theme.color.blue100};
  border-radius: 8px;
  padding: 20px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 20px;
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: ${theme.color.darkBlueGray700};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PillarSelectWrapper = styled.div`
  margin-top: 8px;
`;

const RadioWrapper = styled.div`
  .MuiFormControlLabel-root {
    margin: 0 0 4px 0;
  }
  
  .MuiRadio-root {
    padding: 6px 8px;
  }
`;

const GenerateButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid ${theme.color.blue200};
`;

export type PillarType = {
    id: string;
    node: number;
    label: string;
    code: string;
    prefix: string;
    description: string;
    iconBg: string;
    borderColor: string;
    icon: string;
    iconShadow: string;
    children: {
        id: string;
        node: number;
        label: string;
        count: string;
        icon: string;
        description: string;
    }[];
}[];

type PillarCustomType = {
    label: string;
    value: string;
    selected: boolean;
    index: string;
    graphics: any;
    subdomains: Array<any>;
};

interface Props {
    handleGenerate: any;
    firstVersion: any;
    secondVersion: any;
    compareVersions: boolean;
    differencesOnly: boolean;
     setDifferencesOnly : any;
}
export const GenerateOptions = (props: Props) => {
    const { handleGenerate, firstVersion, secondVersion, compareVersions, differencesOnly, setDifferencesOnly } = props;
    const [singlePillar, setSinglePillar] = useState(false);
    const [selectedPillar, setSelectedPillar] = useState(null);
    const [outputFormat, setOutputFormat] = useState("html");
    const [pillars, setPillars] = useState<Array<PillarCustomType>>([]);

    const { Option, SingleValue } = components;

    const fetchPillars = async () => {
        try {
            const versionRes = await axios.get(`/version?university_code=${firstVersion?.university?.value}&program_name=${firstVersion?.program?.value}`);
            const curriculum_version = versionRes.data.versions.find(
                (element: any) => firstVersion?.version?.value === element?.display_version
            )?.version_number;

            if (!curriculum_version) {
                console.error("No active version found");
                return;
            }

            await axios.put("/version/select", { version_number: curriculum_version });

            const pillarRes = await axios.get(
                `/pillar?curriculum_version=${curriculum_version}&university_code=${firstVersion?.university?.value}`
            );
            mapPillars(pillarRes.data?.pillars);
        } catch (error) {
            console.error("Failed to fetch pillars:", error);
            mapPillars([])
        }
    };

    const mapPillars = (data: PillarType) => {
        const custom: Array<{
            label: string;
            value: string;
            prefix: string;
            selected: boolean;
            index: string;
            graphics: any;
            subdomains: Array<any>;
        }> = [];
        data.forEach((element: any) => {
            // Suppress pillars with prefix "SOF"
            if (element.prefix !== "SOF") {
                custom.push({
                    value: element.id,
                    label: element.title,
                    prefix: element.prefix,
                    selected: false,
                    index: "",
                    graphics: element.graphics,
                    subdomains: element.subdomains,
                });
            }
        });
        setPillars(custom);
    };

    const IconOption = (props: any) => (
        <Option {...props}>
            <MuiIcon
                sx={{
                    mr: 1,
                    padding: "4px",
                    background: props.data.graphics.iconbg,
                    boxShadow: props.data.graphics.iconshadow,
                    borderRadius: "100px",
                    verticalAlign: "bottom",
                    fontSize: "14px",
                    color: 'white'
                }}
            >
                {props.data.graphics.icon}
            </MuiIcon>
            {props.data.label}
        </Option>
    );

    const CustomSingleValue = (props: any) => {
        return (
            <SingleValue {...props}>
                <MuiIcon
                    sx={{
                        mr: 1,
                        padding: "4px",
                        background: props.data.graphics.iconbg,
                        boxShadow: props.data.graphics.iconshadow,
                        borderRadius: "100px",
                        fontSize: "14px",
                        verticalAlign: "sub",
                        color: "white !important"
                    }}
                >
                    {props.data.graphics.icon}
                </MuiIcon>
                <span className="react-select__single-value">
                    {props.selectProps.getOptionLabel(props.data)}
                </span>
            </SingleValue>
        );
    };

    useEffect(()=>{
        fetchPillars()
        setSelectedPillar(null)
        setSinglePillar(false)
    },[firstVersion?.version?.value])
    return (
        <Container>
            <OptionCard>
                <OptionsGrid>
                    <OptionSection>
                        <SectionTitle>Options</SectionTitle>

                        <CheckboxOption>
                            <Checkbox
                                disabled={!firstVersion?.version?.value}
                                checked={singlePillar}
                                onChange={(e) => {
                                    setSinglePillar(e.target.checked)
                                    fetchPillars()
                                }}
                                sx={{
                                    padding: 0,
                                    color: theme.color.blue900,
                                    '&.Mui-checked': {
                                        color: theme.color.blue900,
                                    },
                                }}
                            />
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>Single Pillar</span>
                        </CheckboxOption>

                        {singlePillar && (
                            <PillarSelectWrapper>
                                <SelectBox
                                    isDisabled={!firstVersion?.version?.value}
                                    menuPortalTarget={document.body}
                                    placeholder="Choose pillar..."
                                    onChange={(e: any) => {
                                        setSelectedPillar(e.prefix);
                                    }}
                                    styles={{
                                        placeholder: (base) => ({
                                            ...base,
                                            fontSize: "12px",
                                            textAlign: "left",
                                        }),
                                        menuList: (base) => ({
                                            ...base,
                                            maxHeight: "430px",
                                        }),
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            color: theme.color.lightBlueGray800,
                                        }),
                                    }}
                                    // value={pillars.find((pillar: any) => pillar.prefix === selectedPillar)}
                                    options={pillars}
                                    components={{
                                        Option: IconOption,
                                        SingleValue: CustomSingleValue,
                                    }}
                                    menuPlacement="top"
                                />
                            </PillarSelectWrapper>
                        )}

                        <CheckboxOption>
                            <Checkbox
                                disabled={!compareVersions}
                                checked={differencesOnly}
                                onChange={(e) => setDifferencesOnly(e.target.checked)}
                                sx={{
                                    padding: 0,
                                    color: theme.color.blue900,
                                    '&.Mui-checked': {
                                        color: theme.color.blue900,
                                    },
                                }}
                            />
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>Differences Only</span>
                        </CheckboxOption>
                    </OptionSection>

                    {/* Right Column - Output Format */}
                    <OptionSection>
                        <SectionTitle>Output Format</SectionTitle>

                        <RadioWrapper>
                            <RadioGroup
                                value={outputFormat}
                                onChange={(e) => setOutputFormat(e.target.value)}
                            >
                                <FormControlLabel
                                    value="html"
                                    control={
                                        <Radio
                                            sx={{
                                                color: theme.color.blue900,
                                                '&.Mui-checked': {
                                                    color: theme.color.blue900,
                                                },
                                            }}
                                        />
                                    }
                                    label="HTML"
                                />
                                <FormControlLabel
                                    value="latex"
                                    control={
                                        <Radio
                                            sx={{
                                                color: theme.color.blue900,
                                                '&.Mui-checked': {
                                                    color: theme.color.blue900,
                                                },
                                            }}
                                        />
                                    }
                                    label="Latex"
                                />
                            </RadioGroup>
                        </RadioWrapper>
                    </OptionSection>
                </OptionsGrid>

                <GenerateButtonWrapper>
                    <Button
                        className="reduce_opct_onhover"
                        onClick={() => handleGenerate(
                            singlePillar,
                            selectedPillar,
                            differencesOnly,
                            outputFormat
                        )}
                        disabled={
                            !firstVersion.version || 
                            (compareVersions && !secondVersion.version) ||
                            (singlePillar && !selectedPillar)
                        }
                        style={{
                            whiteSpace: "nowrap",
                            background: 
                                (!firstVersion.version || (compareVersions && !secondVersion.version) || (singlePillar && !selectedPillar))
                                    ? "#2633d44d" 
                                    : theme.color.blueButton,
                            color: "white",
                            boxShadow: 
                                (!firstVersion.version || (compareVersions && !secondVersion.version) || (singlePillar && !selectedPillar))
                                    ? "" 
                                    : "0 2px 5px #808080cc",
                            border: "none",
                            paddingLeft: "20px",
                            paddingRight: "20px",
                        }}
                    >
                        <Icon fontSize="small" style={{ paddingRight: 10 }}>
                            settings_suggest_icon
                        </Icon> Generate
                    </Button>
                </GenerateButtonWrapper>

            </OptionCard>
        </Container>
    );
};